import { _ } from "../../polyglot/polyglot.js";
import { guid } from "../../helpers/string-util.js";
import { select } from "../../elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import Agency from "../../../models/Agency.js";
import ChartOfAccounts from "../../../models/chartOfAccounts/ChartOfAccounts.js";
import regionWidget from "../regionWidget.js";

const SELECT_ACTION_ID = "select-action";
const SELECT_RADIO_BUTTON_ID = "select-radio-button";
const REPLACE_ACTION_ID = "replace-action";
const REPLACE_RADIO_BUTTON_ID = "replace-radio-button";
const CREATE_ACTION_ID = "create-action";
const CREATE_RADIO_BUTTON_ID = "create-radio-button";
const CREATE_TEXT_INPUT_ID = "create-text-input";

/**
 * `ChartOfAccountPicker` is a section in the `selectDataStepWidget` wizard
 * step to choose the chart of account.
 *
 * @param {boolean} [spec.showExtendedActions=false] - Flag for showing
 * extended version with replace and create options.
 */
export default class ChartOfAccountPicker extends Widget {
	constructor({ showExtendedActions = false } = {}) {
		super(...arguments);

		this._showExtendedActions = showExtendedActions;
		this._chartsOfAccounts = [];
		this._importCompanyInfo = null;
		this._hasCurrentChartOfAccounts = false;

		this._validator = () => [];

		this._selection = {
			chartOfAccountsType: null,
			chartOfAccountsId: null,
			chartOfAccountsName: null,
		};

		this._selectChartOfAccountsItems = [];
		this._replaceChartOfAccountsItems = [];

		this._createEvents("onChange");

		this._selectChartOfAccountsDropdown = regionWidget({
			content: (html) => {
				if (!this._selectChartOfAccountsItems.length) return;

				html.render(
					select({
						name: "chart-of-accounts",
						items: this._selectChartOfAccountsItems,
						onChange: this._setSelectionForSelectAction.bind(this),
					}),
				);
			},
		});

		this._replaceChartOfAccountsDropdown = regionWidget({
			content: (html) =>
				html.render(
					select({
						items: this._replaceChartOfAccountsItems,
						onChange: this._setSelectionForReplaceAction.bind(this),
					}),
				),
		});
	}

	reset() {
		this._showExtendedActions = false;
		this._chartsOfAccounts = [];
		this._hasCurrentChartOfAccounts = false;
	}

	showExtendedActions() {
		this._showExtendedActions = true;
		this.update();

		if (this._hasCurrentChartOfAccounts) {
			this.asJQuery()
				.find(`#${REPLACE_RADIO_BUTTON_ID}`)
				.prop("checked", true)
				.change();
		} else {
			this.asJQuery()
				.find(`#${CREATE_RADIO_BUTTON_ID}`)
				.prop("checked", true)
				.change();
		}
	}

	setChartOfAccounts(chartsOfAccounts, currentChartOfAccountsId) {
		this._chartsOfAccounts = ChartOfAccounts.materializeAll(
			chartsOfAccounts,
		).sort((a, b) => a.compare(b));

		this._hasCurrentChartOfAccounts = this._chartsOfAccounts.some(
			(chart) => chart.getId() === currentChartOfAccountsId,
		);

		this._selectChartOfAccountsItems =
			this._createChartOfAccountsDropdownElements(
				this._chartsOfAccounts,
				currentChartOfAccountsId,
			);

		this._selectChartOfAccountsDropdown.update();

		const userDefinedChartOfAccounts = this._chartsOfAccounts.filter(
			(chart) => chart.isUserDefined(),
		);

		this._replaceChartOfAccountsItems =
			this._createChartOfAccountsDropdownElements(
				userDefinedChartOfAccounts,
				currentChartOfAccountsId,
			);

		this._replaceChartOfAccountsDropdown.update();

		this._showSelectAction();
	}

	setImportCompanyInfo(importCompanyInfo) {
		this._importCompanyInfo = importCompanyInfo;
		this.update();
	}

	getChartOfAccount() {
		return this._selection;
	}

	validate(onSuccess, onError) {
		const errors = this._validator();
		if (errors.length) {
			onError(errors);
		} else {
			onSuccess();
		}
	}

	renderContentOn(html) {
		if (this._showExtendedActions) {
			this._renderExtendedContentOn(html);
		} else {
			this._renderSimpleContentOn(html);
		}
	}

	_renderSimpleContentOn(html) {
		html.div(
			{ klass: "form-group chart-of-account" },
			html.label(
				{ klass: "control-label col-sm-4" },
				_("Chart of Accounts"),
			),
			html.div(
				{ klass: "col-sm-8 check-group" },
				html.div(
					{
						klass: "select-chartOfAccounts",
						"data-test": "select-chart-of-account",
					},
					this._selectChartOfAccountsDropdown,
				),
			),
		);
	}

	_renderExtendedContentOn(html) {
		html.div(
			{ klass: "form-group chart-of-account" },
			html.label(
				{ klass: "control-label col-sm-4" },
				_("Chart of Accounts"),
			),
			html.div(
				{ klass: "col-sm-8 check-group" },
				this._renderReplaceChartOfAccountsOn.bind(this),
				this._renderCreateChartOfAccountsOn.bind(this),
				this._renderSelectChartOfAccountsOn.bind(this),
			),
		);
	}

	_renderSelectChartOfAccountsOn(html) {
		html.div(
			html.label(
				html.input({
					id: SELECT_RADIO_BUTTON_ID,
					klass: "form-control",
					type: "radio",
					name: "chartOfAccountsAction",
					change: this._showSelectAction.bind(this),
				}),
				_("Select existing chart of accounts"),
			),
			html.div(
				{ id: SELECT_ACTION_ID },
				this._selectChartOfAccountsDropdown,
			),
		);
	}

	_renderReplaceChartOfAccountsOn(html) {
		if (!this._replaceChartOfAccountsItems.length) {
			return;
		}

		html.div(
			html.label(
				html.input({
					id: REPLACE_RADIO_BUTTON_ID,
					klass: "form-control",
					type: "radio",
					name: "chartOfAccountsAction",
					checked: this._hasCurrentChartOfAccounts,
					change: this._showReplaceAction.bind(this),
				}),
				_("Import and replace existing chart of accounts"),
			),
			html.div(
				{ id: REPLACE_ACTION_ID },
				this._replaceChartOfAccountsDropdown,
			),
		);
	}

	_renderCreateChartOfAccountsOn(html) {
		html.div(
			html.label(
				html.input({
					id: CREATE_RADIO_BUTTON_ID,
					klass: "form-control",
					type: "radio",
					name: "chartOfAccountsAction",
					checked: !this._hasCurrentChartOfAccounts,
					change: this._showCreateAction.bind(this),
				}),
				_("Import and create new chart of accounts"),
			),
			html.div(
				{ id: CREATE_ACTION_ID, klass: "create-chartOfAccounts" },
				html
					.input({
						id: CREATE_TEXT_INPUT_ID,
						klass: "form-control",
						type: "text",
						name: "chartOfAccounts-name",
						placeholder: _("New chart of accounts name"),
						value: this._getDefaultCoANameFromCompanyInfo(),
					})
					.on("keyup", () => {
						this._setSelectionForCreateAction();
						this.onChange.trigger();
					}),
			),
		);
	}

	_getDefaultCoANameFromCompanyInfo() {
		if (!this._importCompanyInfo) {
			return "";
		}
		return this.constructor.defaultCoANameFromCompanyName(
			this._importCompanyInfo.companyName,
		);
	}

	_showSelectAction() {
		this._hideAllActions();
		this.asJQuery().find(`#${SELECT_ACTION_ID}`).show();
		this._setValidatorForSelectAction();
		this._setSelectionForSelectAction();
		this.onChange.trigger();
	}

	_setValidatorForSelectAction() {
		this._validator = () => {
			if (!this._selection.chartOfAccountsId) {
				return [
					{
						type: _("Invalid chart of accounts"),
						message: _("A chart of accounts must be selected"),
					},
				];
			}
			return [];
		};
	}

	_setSelectionForSelectAction(chartOfAccount) {
		chartOfAccount ??= this._selectChartOfAccountsItems.find(
			(each) => each.selected,
		)?.value;

		this._selection = {
			chartOfAccountsType: "selectChartOfAccounts",
			chartOfAccountsId: chartOfAccount?.id,
			chartOfAccountsName: chartOfAccount?.name,
		};
	}

	_showReplaceAction() {
		this._hideAllActions();
		this.asJQuery().find(`#${REPLACE_ACTION_ID}`).show();
		this._setValidatorForReplaceAction();
		this._setSelectionForReplaceAction();
		this.onChange.trigger();
	}

	// eslint-disable-next-line sonarjs/no-identical-functions -- we should fix that
	_setValidatorForReplaceAction() {
		this._validator = () => {
			if (!this._selection.chartOfAccountsId) {
				return [
					{
						type: _("Invalid chart of accounts"),
						message: _("A chart of accounts must be selected"),
					},
				];
			}
			return [];
		};
	}

	_setSelectionForReplaceAction(chartOfAccount) {
		this._selection = chartOfAccount && {
			chartOfAccountsType: "replaceChartOfAccounts",
			chartOfAccountsId: chartOfAccount.id,
			chartOfAccountsName: chartOfAccount.name,
		};
	}

	_showCreateAction() {
		this._hideAllActions();
		this.asJQuery().find(`#${CREATE_ACTION_ID}`).show();
		this._setValidatorForCreateAction();
		this._setSelectionForCreateAction();
		this.asJQuery().find(`#${CREATE_TEXT_INPUT_ID}`).focus();
	}

	_setValidatorForCreateAction() {
		this._validator = () => {
			const name = this.asJQuery().find(`#${CREATE_TEXT_INPUT_ID}`).val();
			if (!name) {
				return [
					{
						type: _("Invalid name"),
						message: _("Chart of Accounts name can not be empty"),
					},
				];
			} else if (
				this._chartsOfAccounts.some((chart) => chart.getName() === name)
			) {
				return [
					{
						type: _("Invalid name"),
						message: _("Chart of Accounts name is already used"),
					},
				];
			}
			return [];
		};
	}

	_setSelectionForCreateAction() {
		this._selection = {
			chartOfAccountsType: "createChartOfAccounts",
			chartOfAccountsId: guid(),
			chartOfAccountsName: this.asJQuery()
				.find(`#${CREATE_TEXT_INPUT_ID}`)
				.val(),
		};
	}

	_hideAllActions() {
		this.asJQuery().find(`#${SELECT_ACTION_ID}`).hide();
		this.asJQuery().find(`#${REPLACE_ACTION_ID}`).hide();
		this.asJQuery().find(`#${CREATE_ACTION_ID}`).hide();
	}

	_createChartOfAccountsDropdownElements(
		chartOfAccounts,
		currentChartOfAccountsId,
	) {
		const elements = chartOfAccounts.map((chart) =>
			this._chartOfAccountToDropdownItem(chart, currentChartOfAccountsId),
		);
		if (
			elements.length > 0 &&
			!elements.some((element) => element.selected)
		) {
			elements[0].selected = true;
		}
		return elements;
	}

	_chartOfAccountToDropdownItem(chart, currentChartOfAccountsId) {
		const selected = chart.getId() === currentChartOfAccountsId;

		return {
			value: { id: chart.getId(), name: chart.getName() },
			label: this._makeChartOfAccountsName(chart),
			selected,
		};
	}

	_makeChartOfAccountsName(chartOfAccounts) {
		const name = chartOfAccounts.getName();

		if (chartOfAccounts.getOwnerType() === Agency.getType())
			return _("{entityName} (shared)", { entityName: name });

		return name;
	}

	static defaultChartOfAccountsConfiguration({
		companyInfo,
		chartOfAccounts = [],
	}) {
		const companyDefaultCoA = chartOfAccounts.find(
			(coa) => coa.isUserDefined,
		);

		const chartOfAccountsId = companyDefaultCoA
			? companyDefaultCoA.id
			: guid();

		return {
			// "replace" type will update an existing coa or create a
			// new one:
			chartOfAccountsType: "replaceChartOfAccounts",
			chartOfAccountsId,
			chartOfAccountsName: this.defaultCoANameFromCompanyName(
				companyInfo && companyInfo.companyName,
			),
		};
	}

	static defaultCoANameFromCompanyName(companyName) {
		return _("{companyName} - imported COA", {
			companyName,
		}).toString();
	}
}
